import { AxiosAdapter, UserAdapter } from '@adapters';
import { useCheckIdDuplicate } from '@composables';
import { SignUpPayload } from '@core/dto';
import {
  DORMANT_ACCOUNT_CONVERSION_CODE,
  GENDER_CODE,
  LUNAR_CODE,
  STATUS_YN,
} from '@core/enums';
import { SignUpMemberUsecase, SignUpUsecase } from '@core/useCases';
import {
  getBirthdayWithHyphen,
  isBirthday,
  isEmail,
  isMobileNumber,
  isPwd,
  isWebId,
} from '@core/utils';
import { useAlertStore, useUserStore } from '@stores';
import { computed, reactive, ref, watch } from 'vue';

const genderOptions = [
  { value: GENDER_CODE.MALE, label: '남성' },
  { value: GENDER_CODE.FEMALE, label: '여성' },
];
const smsAgreeOptions = [
  { value: STATUS_YN.YES, label: '수신동의' },
  { value: STATUS_YN.NO, label: '수신거부' },
];
const lunarOptions = [
  { value: LUNAR_CODE.SOLAR, label: '양력' },
  { value: LUNAR_CODE.LUNAR, label: '음력' },
];
const marketingAgreeOptions = [
  { value: true, label: '이메일수신 동의' },
  { value: false, label: '이메일수신 미동의' },
];
const emailAgreeOptions = [
  { value: true, label: '수신동의' },
  { value: false, label: '수신거부' },
];
const personalInfoExpiryOptions = [
  { value: DORMANT_ACCOUNT_CONVERSION_CODE.UNTIL_OUT, label: '탈퇴 시까지' },
  { value: DORMANT_ACCOUNT_CONVERSION_CODE.YEAR, label: '1년 후 파기' },
];

export const useSignUp = () => {
  // ================================================================= 이하 회원가입시 약관동의 관련 코드
  const userStore = useUserStore();
  const allAgreeCheck = ref(false);
  const usageAgreeCheck = ref(false);
  const privateThirdPartyAgreeCheck = ref(false);
  const privateProvideAgreeCheck = ref(false);
  const privateOutSourcingAgreeCheck = ref(false);
  const privateAgreeCheck = ref(false);
  const marketingAgreeCheck = ref(userStore.marketingAgree);

  const agrees = reactive([
    {
      id: 'usageAgreeCheck',
      title: '이용약관',
      value: usageAgreeCheck,
      required: true,
      // visible: false,
      // content: usageAgree,
    },
    {
      id: 'privateOutSourcingAgreeCheck',
      title: '개인정보 취급 위탁 동의',
      value: privateOutSourcingAgreeCheck,
      required: true,
      // visible: false,
      // content: privatePolicy,
    },
    {
      id: 'privateThirdPartyAgreeCheck',
      title: '개인정보 제3자 제공 동의',
      value: privateThirdPartyAgreeCheck,
      required: true,
      // visible: false,
      // content: personalInfoConsent,
    },
    {
      id: 'privateProvideAgreeCheck',
      title: '개인정보의 제3자 제공에 관한 사항',
      value: privateProvideAgreeCheck,
      required: true,
      // visible: false,
      // content: personalInfoConsent,
    },
    {
      id: 'privateAgreeCheck',
      title: '개인정보수집동의',
      value: privateAgreeCheck,
      required: true,
      // visible: false,
      // content: personalInfoConsent,
    },
    {
      id: 'marketingAgreeCheck',
      title: '마케팅 및 광고 활용 동의',
      value: marketingAgreeCheck,
      required: false,
      // visible: false,
      // content: advertisementConsent,
    },
  ]);

  const validateAgree = () => {
    if (!usageAgreeCheck.value) {
      return '서비스 이용약관에 동의해주세요';
    }
    if (!privateAgreeCheck.value) {
      return '개인정보수집에 동의해주세요';
    }
    if (!privateProvideAgreeCheck.value) {
      return '개인정보의 제3자 제공에 관한 사항을 동의해주세요';
    }
    if (!privateThirdPartyAgreeCheck.value) {
      return '개인정보 제3자 제공에 동의해주세요';
    }
    if (!privateOutSourcingAgreeCheck.value) {
      return '개인정보 취급 위탁에 동의해주세요';
    }
    return true;
  };

  const handleChangeAllCheck = (checked: Event | boolean) => {
    const isChecked =
      typeof checked === 'boolean' ? checked : allAgreeCheck.value;
    allAgreeCheck.value =
      usageAgreeCheck.value =
      privateAgreeCheck.value =
      privateProvideAgreeCheck.value =
      privateThirdPartyAgreeCheck.value =
      privateOutSourcingAgreeCheck.value =
      marketingAgreeCheck.value =
        isChecked;
  };

  const isRequiredAgreeAllTrue = computed(() => {
    return (
      usageAgreeCheck.value &&
      privateProvideAgreeCheck.value &&
      privateThirdPartyAgreeCheck.value &&
      // privatePolicyCheck.value &&
      privateAgreeCheck.value
    );
  });

  watch(
    [
      usageAgreeCheck,
      privateAgreeCheck,
      privateProvideAgreeCheck,
      privateThirdPartyAgreeCheck,
      privateOutSourcingAgreeCheck,
      marketingAgreeCheck,
    ],
    () => {
      allAgreeCheck.value =
        usageAgreeCheck.value &&
        privateAgreeCheck.value &&
        privateProvideAgreeCheck.value &&
        privateThirdPartyAgreeCheck.value &&
        privateOutSourcingAgreeCheck.value &&
        marketingAgreeCheck.value;
    }
  );

  // ================================================================= 이하 회원가입시 정보입력 관련 코드
  const axiosAdapter = new AxiosAdapter();
  const userAdapter = new UserAdapter(axiosAdapter);
  const { handleCheckIdDuplicate: checkIdDuplicate } = useCheckIdDuplicate();
  const alertStore = useAlertStore();

  const webPwdConfirm = ref('');
  const minLength = 4;
  const maxLength = 12;
  const userInfo: SignUpPayload = reactive({
    memberName:
      (userStore.memberName || sessionStorage.getItem('regularMemberName')) ??
      '',
    hpNo: userStore.hpNo,
    webId: '',
    webPwd: '',
    birthDay: '',
    sexCode: GENDER_CODE.MALE,
    lunarCode: LUNAR_CODE.SOLAR,
    areaCode: '' as string,
    sigunCode: '' as string,
    email: '',
    emailRecptnAgre: true,
    smsRecptnDiv: userStore.isPrivacyUseAgreement
      ? STATUS_YN.YES
      : STATUS_YN.NO,
    marktInfoAgreFlag: userStore.marketingAgree,
    certificationTransaction: {
      userdi: '',
      userci: '',
    },
    hpNoCertificationToken: '', // 외부인증없는경우만 필요
  });
  const isAvailableId = ref(false);
  const isPasswordMatch = computed(() => {
    return userInfo.webPwd === webPwdConfirm.value;
  });
  const snsKind = ref(sessionStorage.getItem('snsKind') ?? '');
  const memberId = ref('');
  const cprDiv = ref('');

  const handleCheckIdDuplicate = async () => {
    isAvailableId.value = false;
    isAvailableId.value = await checkIdDuplicate(
      userInfo.webId,
      minLength,
      maxLength
    );
  };

  const signUp = async () => {
    const signUpUsecase = new SignUpUsecase(userAdapter);
    const isNice = sessionStorage.getItem('signupType') === 'nice';
    const { hpNoCertificationToken, ...filteredUserInfo } = userInfo;
    await signUpUsecase.execute({
      ...(isNice ? filteredUserInfo : userInfo),
      birthDay: getBirthdayWithHyphen(userInfo.birthDay),
    });
  };

  const signUpMember = async () => {
    const signUpMemberUsecase = new SignUpMemberUsecase(userAdapter);
    await signUpMemberUsecase.execute({
      ...userInfo,
      birthDay: getBirthdayWithHyphen(userInfo.birthDay),
      memberId: memberId.value,
      cprDiv: cprDiv.value,
    });
  };

  const validateSignUpForm = async (
    isVerifyComplete: boolean
  ): Promise<boolean> => {
    if (!isWebId(userInfo.webId, minLength, maxLength)) {
      await alertStore.showAlertDialog(
        `아이디는 영문이나 영문+숫자로 <br />${minLength}~${maxLength}자 이내로 입력해주세요.`
      );
      return false;
    }
    if (!isAvailableId.value) {
      await alertStore.showAlertDialog('아이디 중복확인을 해주세요.');
      return false;
    }
    if (!userInfo.webPwd) {
      await alertStore.showAlertDialog('비밀번호가 입력되지 않았습니다');
      return false;
    }
    if (!isPwd({ pwd: userInfo.webPwd, type: 'A', min: 8, max: 12 })) {
      await alertStore.showAlertDialog(
        '비밀번호는 영어/숫자/특수문자를 조합하여<br/> 8~12자 이내로 입력해주세요.'
      );
      return false;
    }
    if (!webPwdConfirm.value) {
      await alertStore.showAlertDialog('비밀번호 확인을 입력해 주세요');
      return false;
    }
    if (!isPasswordMatch.value) {
      await alertStore.showAlertDialog('비밀번호가 일치하지 않습니다.');
      return false;
    }
    if (!userInfo.memberName) {
      await alertStore.showAlertDialog('이름을 입력해주세요.');
      return false;
    }
    if (!isBirthday(userInfo.birthDay)) {
      await alertStore.showAlertDialog('생년월일을 확인해주세요.');
      return false;
    }
    if (userInfo.email && !isEmail(userInfo.email)) {
      await alertStore.showAlertDialog('이메일을 확인해주세요.');
      return false;
    }
    // if (!userInfo.sexCode) {
    //   await alertStore.showAlertDialog('성별을 선택하여주세요');
    //   return false;
    // }
    if (!isMobileNumber(userInfo.hpNo)) {
      await alertStore.showAlertDialog('휴대폰 번호를 확인해주세요.');
      return false;
    }
    if (!isVerifyComplete) {
      await alertStore.showAlertDialog('본인인증을 진행해주세요.');
      return false;
    }
    // if (!userInfo.homePostNo || !userInfo.homeAddr1) {
    //   await alertStore.showAlertDialog('주소를 입력해주세요.');
    //   return false;
    // }
    return true;
  };

  return {
    // 약관동의 return
    agrees,
    isRequiredAgreeAllTrue,
    handleChangeAllCheck,

    // 회원가입 약관동의 return
    allAgreeCheck,
    usageAgreeCheck,
    privateProvideAgreeCheck,
    privateThirdPartyAgreeCheck,
    privateOutSourcingAgreeCheck,
    privateAgreeCheck,
    marketingAgreeCheck,
    validateAgree,

    // 회원가입 폼 return
    userInfo,
    webPwdConfirm,
    genderOptions,
    smsAgreeOptions,
    lunarOptions,
    marketingAgreeOptions,
    emailAgreeOptions,
    personalInfoExpiryOptions,
    isAvailableId,
    isPasswordMatch,
    snsKind,
    memberId,
    cprDiv,
    validateSignUpForm,
    signUp,
    signUpMember,
    handleCheckIdDuplicate,
  };
};
